import React, { useState } from 'react';
import { api, errorText } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { day } from '../lib/format.js';
import { useAppVersion } from '../lib/version.js';
import { Alert, Card, CardSkeleton, Empty, Field, Pill, ThemeToggle, useAsync } from '../components/ui.jsx';
import { Renewals, WorkspaceHealth } from './PlatformHealth.jsx';
import ChangePassword from '../components/ChangePassword.jsx';

const initials = (name) => (name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

/** What a workspace can be sold beyond billing; mirrors FEATURES on the server. */
const FEATURES = [
  ['tally_dashboard', 'Tally dashboard', "Ledger Shield's dashboard of the client's books, inside InvIT"],
  ['tally_reports', 'Tally reports', 'Trial balance, P&L, balance sheet, ledgers, day book, outstandings'],
];

const isoToday = () => new Date(Date.now() + 330 * 60_000).toISOString().slice(0, 10);
const addYear = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() + 1);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};
const blankSubscription = () => ({ starts_on: isoToday(), ends_on: addYear(isoToday()), features: [] });

function SubscriptionFields({ value, onChange }) {
  const toggle = (key, on) => onChange({
    ...value, features: on ? [...value.features, key] : value.features.filter((f) => f !== key),
  });
  return (
    <>
      <div className="grid fields">
        <Field label="Subscription starts">
          <input type="date" value={value.starts_on || ''} onChange={(e) => onChange({ ...value, starts_on: e.target.value || null })} />
        </Field>
        <Field label="Subscription ends" hint="last day of service; empty = open-ended">
          <input type="date" value={value.ends_on || ''} onChange={(e) => onChange({ ...value, ends_on: e.target.value || null })} />
        </Field>
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        {FEATURES.map(([key, label, note]) => (
          <label key={key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: 'var(--ink)', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
            <input type="checkbox" style={{ width: 16, marginTop: 3 }} checked={value.features.includes(key)}
              onChange={(e) => toggle(key, e.target.checked)} />
            <span><b style={{ fontWeight: 600 }}>{label}</b><div className="card-note">{note}</div></span>
          </label>
        ))}
      </div>
    </>
  );
}

const subscriptionText = (t) => {
  if (t.tenant_access === 'pending') return `starts ${day(t.subscription_starts_on)}`;
  if (!t.subscription_ends_on) return 'open-ended';
  if (t.tenant_access === 'expired') return `ended ${day(t.subscription_ends_on)}`;
  return `to ${day(t.subscription_ends_on)} · ${t.days_left} day${t.days_left === 1 ? '' : 's'} left`;
};

const BLANK = {
  name: '', slug: '',
  entity: { name: '', gstin: '', state_code: '', address_line1: '', city: '', pincode: '' },
  admin: { full_name: '', email: '', password: '' },
};

/**
 * The platform account owns no workspace of its own, so it gets its own console
 * rather than a tenant dashboard it has no permission to read: the client list,
 * onboarding, and the agent token that onboarding issues exactly once.
 */
export default function Platform({ onSignOut }) {
  const session = useSession();
  const version = useAppVersion();
  const tenants = useAsync(() => api.get('/api/admin/tenants'), []);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // { tenant, value }
  const [view, setView] = useState('workspaces');
  const VIEWS = [['workspaces', 'Workspaces'], ['health', 'Health'], ['renewals', 'Renewals']];

  const set = (path, value) => setForm((f) => {
    const next = { ...f };
    if (path.includes('.')) {
      const [group, key] = path.split('.');
      next[group] = { ...next[group], [key]: value };
    } else {
      next[path] = value;
      // The slug is what the client is known by everywhere else; derive it while
      // it is untouched rather than asking for the same thing twice.
      if (path === 'name' && !f.slugTouched) next.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const { slugTouched, ...body } = form;
      // An optional field left blank must be absent, not an empty string: the
      // GSTIN is validated as exactly 15 characters when it is there at all.
      body.entity = Object.fromEntries(Object.entries(body.entity).filter(([, v]) => v !== ''));
      const res = await api.post('/api/admin/tenants', body);
      setCreated(res);
      setForm(null);
      tenants.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const saveSubscription = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.patch(`/api/admin/tenants/${editing.tenant.id}/subscription`, editing.value);
      setEditing(null);
      tenants.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const manage = (t) => setEditing({
    tenant: t,
    value: { starts_on: t.subscription_starts_on, ends_on: t.subscription_ends_on, features: t.features || [] },
  });

  const setStatus = async (tenant, status) => {
    setError(null);
    try {
      await api.post(`/api/admin/tenants/${tenant.id}/status`, { status });
      tenants.reload();
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="shell">
      <nav className="rail" aria-label="Main">
        <div className="rail-brand">
          <div className="rail-mark">IV</div>
          <div>
            <div className="name">InvIT</div>
            <div className="org">Platform</div>
          </div>
        </div>
        <div className="rail-group">Overview</div>
        {VIEWS.map(([key, label]) => (
          <a key={key} className={`nav ${view === key ? 'active' : ''}`} href={`#${key}`}
            onClick={(e) => { e.preventDefault(); setView(key); }}>{label}</a>
        ))}

        <div className="grow" />
        {version && <div className="rail-version" title="Build version">InvIT v{version}</div>}
        <div className="rail-foot">
          <div className="rail-avatar">{initials(session.user.full_name)}</div>
          <div className="who">
            {session.user.full_name}
            <span>platform</span>
          </div>
          <button className="icon-btn" style={{ marginLeft: 'auto' }} title="Change password"
            aria-label="Change password" onClick={() => setPasswordOpen(true)}>🔑</button>
          <button className="icon-btn" title="Sign out" aria-label="Sign out"
            onClick={onSignOut}>⎋</button>
        </div>
      </nav>

      <div className="content">
        <header className="topbar">
          <div className="crumb">InvIT / <b>{VIEWS.find(([k]) => k === view)[1]}</b></div>
          <div className="grow" />
          <ThemeToggle />
        </header>

        <main className="page">
          {view === 'health' && <WorkspaceHealth />}
          {view === 'renewals' && <Renewals />}
          {view === 'workspaces' && <>
          <div className="page-head">
            <div>
              <h1 className="page-title">Workspaces</h1>
              <div className="page-sub">
                Every client is one workspace, isolated from the others. Onboarding creates its first entity,
                its administrator and the token its Tally sync agent authenticates with.
              </div>
            </div>
            {!form && (
              <button className="btn" onClick={() => { setCreated(null); setEditing(null); setForm({ ...BLANK, subscription: blankSubscription() }); }}>
                ＋ Onboard a client
              </button>
            )}
          </div>

          <Alert onRetry={() => setError(null)}>{error}</Alert>

          {created && (
            <Card title={`${created.tenant.name} is ready`}
              note="The agent token is shown once and never again. Put it in that client's agent .env as INVIT_AGENT_TOKEN.">
              <div className="mono token-once">{created.agent.token}</div>
              <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>
                Administrator: {created.user.email} · Entity: {created.entity.name}
              </div>
              <div style={{ marginTop: 14 }}>
                <button className="btn small secondary" onClick={() => setCreated(null)}>
                  I have saved the token
                </button>
              </div>
            </Card>
          )}

          {form && (
            <Card title="Onboard a client" note="The administrator can change everything else once they sign in.">
              <form onSubmit={submit}>
                <div className="grid fields">
                  <Field label="Client name">
                    <input value={form.name} autoFocus required onChange={(e) => set('name', e.target.value)} />
                  </Field>
                  <Field label="Workspace slug" hint="lowercase, no spaces">
                    <input value={form.slug} required pattern="[a-z0-9-]+"
                      onChange={(e) => { setForm((f) => ({ ...f, slugTouched: true })); set('slug', e.target.value); }} />
                  </Field>
                  <Field label="Entity name" hint="as it appears on the invoice">
                    <input value={form.entity.name} required onChange={(e) => set('entity.name', e.target.value)} />
                  </Field>
                  <Field label="GSTIN">
                    <input value={form.entity.gstin} onChange={(e) => set('entity.gstin', e.target.value.toUpperCase())} />
                  </Field>
                  <Field label="State code" hint="two digits, e.g. 19 for West Bengal">
                    <input value={form.entity.state_code} required pattern="[0-9]{2}"
                      onChange={(e) => set('entity.state_code', e.target.value)} />
                  </Field>
                  <Field label="Administrator name">
                    <input value={form.admin.full_name} required onChange={(e) => set('admin.full_name', e.target.value)} />
                  </Field>
                  <Field label="Administrator email">
                    <input type="email" value={form.admin.email} required onChange={(e) => set('admin.email', e.target.value)} />
                  </Field>
                  <Field label="Temporary password" hint="they are asked to change it at first sign in">
                    <input type="password" value={form.admin.password} required minLength={10}
                      onChange={(e) => set('admin.password', e.target.value)} />
                  </Field>
                </div>
                <hr className="hairline" />
                <SubscriptionFields value={form.subscription} onChange={(v) => setForm((f) => ({ ...f, subscription: v }))} />
                <div className="inline" style={{ marginTop: 16 }}>
                  <button className="btn" disabled={busy}>{busy ? 'Creating…' : 'Create workspace'}</button>
                  <button type="button" className="btn ghost" onClick={() => setForm(null)}>Cancel</button>
                </div>
              </form>
            </Card>
          )}

          {editing && (
            <Card title={`Subscription · ${editing.tenant.name}`}
              note="Sign-in, the Tally sync and the Ledger Shield licence on the client's PC all follow these dates. An expired workspace keeps its data.">
              <form onSubmit={saveSubscription}>
                <SubscriptionFields value={editing.value} onChange={(v) => setEditing((x) => ({ ...x, value: v }))} />
                <div className="inline" style={{ marginTop: 16 }}>
                  <button className="btn" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
                  <button type="button" className="btn secondary"
                    onClick={() => setEditing((x) => {
                      const from = x.value.ends_on && x.value.ends_on >= isoToday() ? x.value.ends_on : isoToday();
                      const next = new Date(`${from}T00:00:00Z`); next.setUTCDate(next.getUTCDate() + 1);
                      return { ...x, value: { ...x.value, ends_on: addYear(next.toISOString().slice(0, 10)) } };
                    })}>
                    Renew for a year
                  </button>
                  <button type="button" className="btn ghost" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </form>
            </Card>
          )}

          {tenants.loading && !tenants.data && <CardSkeleton lines={4} />}
          {tenants.error && <Alert onRetry={tenants.reload}>{tenants.error}</Alert>}

          {tenants.data && (
            <Card pad={tenants.data.tenants.length === 0}>
              {tenants.data.tenants.length === 0 ? (
                <Empty title="No clients yet">Onboard the first one to get its workspace and sync agent.</Empty>
              ) : (
                <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
                  <table>
                    <thead>
                      <tr><th>Client</th><th>Slug</th><th className="r">Users</th><th className="r">Agents</th>
                        <th>Onboarded</th><th>Subscription</th><th>Status</th><th /></tr>
                    </thead>
                    <tbody>
                      {tenants.data.tenants.map((t) => (
                        <tr key={t.id}>
                          <td>{t.name}</td>
                          <td className="mono">{t.slug}</td>
                          <td className="r num">{t.users}</td>
                          <td className="r num">{t.agents}</td>
                          <td>{day(t.created_at)}</td>
                          <td>
                            {subscriptionText(t)}
                            {(t.features || []).length > 0 && (
                              <div className="card-note">{t.features.map((f) => (FEATURES.find(([k]) => k === f) || [f, f])[1]).join(' · ')}</div>
                            )}
                          </td>
                          <td><Pill value={t.status === 'active' ? t.tenant_access : t.status} /></td>
                          <td className="r" style={{ whiteSpace: 'nowrap' }}>
                            <button className="btn small secondary" style={{ marginRight: 6 }}
                              onClick={() => { setForm(null); manage(t); }}>Subscription</button>
                            <button className="btn small secondary"
                              onClick={() => setStatus(t, t.status === 'active' ? 'suspended' : 'active')}>
                              {t.status === 'active' ? 'Suspend' : 'Reactivate'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
          </>}
        </main>
      </div>

      {passwordOpen && <ChangePassword onClose={() => setPasswordOpen(false)} />}
    </div>
  );
}
